import { unwrapApiData, type ApiClient } from '@klareospace/pharos-lib-front-utils';
import type { Kpi, KpiKey, PharosApiResponse } from '@klareospace/pharos-lib-types';
import { KPI_GRANULARITY, KPI_KEYS } from '@klareospace/pharos-lib-types';

// ─── Types ────────────────────────────────────────────────────────────────────

/** `GET /kpis/order-kpis/day/:year/:month/:day` params — `year`/`month`/`day` are UTC calendar values. */
export interface GetOrderKpisDayParams {
  day: number;
  month: number;
  siteId?: string;
  spaceId?: string;
  year: number;
}

/** `GET /kpis/driver-task-kpis/day/:year/:month/:day` params — `year`/`month`/`day` are UTC calendar values. */
export type GetDriverKpisDayParams = GetOrderKpisDayParams;

/** `GET /kpis/:key/mtd/:year/:month` params — shared by `order-kpis`/`driver-task-kpis`. */
export interface GetKpisMtdParams {
  month: number;
  siteId?: string;
  spaceId?: string;
  year: number;
}

export interface KpisApi {
  getDriverKpisDay: (params: GetDriverKpisDayParams) => Promise<Kpi>;
  getDriverKpisMtd: (params: GetKpisMtdParams) => Promise<Kpi>;
  getOrderKpisDay: (params: GetOrderKpisDayParams) => Promise<Kpi>;
  getOrderKpisMtd: (params: GetKpisMtdParams) => Promise<Kpi>;
}

// ─── API ────────────────────────────────────────────────────────────────────

export function createKpisApi(client: ApiClient): KpisApi {
  return {
    /** `GET /kpis/driver-task-kpis/day/:year/:month/:day` — same 202-then-200 convention as `getOrderKpisDay`. */
    getDriverKpisDay: async ({ day, month, siteId, spaceId, year }: GetDriverKpisDayParams): Promise<Kpi> => {
      const response = await client.execute<PharosApiResponse<Kpi>>(() =>
        client.instance.get(`/kpis/driver-task-kpis/day/${year}/${month}/${day}`, { params: { siteId, spaceId } }),
      );
      return unwrapApiData(response, 'getDriverKpisDay');
    },

    /** `GET /kpis/driver-task-kpis/mtd/:year/:month` — see `getKpiMtd` for the shared 202/200 convention. */
    getDriverKpisMtd: (params: GetKpisMtdParams): Promise<Kpi> =>
      getKpiMtd(client, KPI_KEYS.DRIVER_TASK_KPIS, params, 'getDriverKpisMtd'),

    /** `GET /kpis/order-kpis/day/:year/:month/:day` — 202 means still computing: check `metrics === undefined`, not the status code. */
    getOrderKpisDay: async ({ day, month, siteId, spaceId, year }: GetOrderKpisDayParams): Promise<Kpi> => {
      const response = await client.execute<PharosApiResponse<Kpi>>(() =>
        client.instance.get(`/kpis/order-kpis/day/${year}/${month}/${day}`, { params: { siteId, spaceId } }),
      );
      return unwrapApiData(response, 'getOrderKpisDay');
    },

    /** `GET /kpis/order-kpis/mtd/:year/:month` — see `getKpiMtd` for the shared 202/200 convention. */
    getOrderKpisMtd: (params: GetKpisMtdParams): Promise<Kpi> =>
      getKpiMtd(client, KPI_KEYS.ORDER_KPIS, params, 'getOrderKpisMtd'),
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Shared `GET /kpis/:key/mtd/:year/:month` impl — same 202/200 convention as `getOrderKpisDay`. */
async function getKpiMtd(
  client: ApiClient,
  key: KpiKey,
  { month, siteId, spaceId, year }: GetKpisMtdParams,
  methodName: string,
): Promise<Kpi> {
  const response = await client.execute<PharosApiResponse<Kpi>>(() =>
    client.instance.get(`/kpis/${key}/${KPI_GRANULARITY.MONTH_TO_DATE}/${year}/${month}`, {
      params: { siteId, spaceId },
    }),
  );
  return unwrapApiData(response, methodName);
}
